//Built-in libraries

//Third party libraries
import { InformationCircleIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { type ChangeEvent, useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import CheckboxGroup from '@/components/UI/Form/CheckboxGroup';
import InputError from '@/components/UI/Form/Error';
import Input from '@/components/UI/Form/Input';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { formatNumber, isObjectEmpty } from '@/utils';

//STP Libraries

import { Accordion, AccordionItem } from '@/components/UI/Accordion';
import Modal from '@/components/UI/Modal';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
//Assets and Miscellaneous
import type { ReactSelectOption } from '@/types/Constants';
import { States } from '../../../../../../../constants';
import { companyRoles, constructionTypes, initialValues, projectTypes } from './constant';
import type { ICompanyCompletedProject } from './types';

//Component Begin

const CompanyCompletedProjectForm = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editProjectId, setEditProjectId] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<ICompanyCompletedProject>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [showModal, setShowModal] = useState({
    addProject: false,
  });
  const [companyCompletedProject, setCompanyCompletedProject] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setCompanyCompletedProject(initialValues);
  };

  const editProjectHandler = (project: any) => {
    setIsEditing(true);
    const formData = Object.keys(companyCompletedProject).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...companyCompletedProject[next],
          value: project[next],
          isTouched: !!project[next],
        },
      };
    }, {});
    setCompanyCompletedProject(formData);
    setEditProjectId(project.id);
    handleModal('addProject');
  };

  const updateProjectHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(companyCompletedProject).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: companyCompletedProject[next].value,
            id: editProjectId,
          };
        },
        {}
      );
      updateData({
        experience: {
          ...data.experience,
          companyCompleted: data.experience.companyCompleted.map((p: any) => {
            if (p.id === editProjectId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
      handleModal('addProject');
    }
  };

  const removeProjectHandler = (project: ICompanyCompletedProject) => {
    setShowRemoveConfirm(true);
    setSelectedProject(project);
  };
  const confirmRemoveProject = async () => {
    setShowRemoveConfirm(false);
    const filteredProjects = data.experience.companyCompleted.filter((project: any) => project.id !== selectedProject?.id);
    updateData({
      experience: {
        ...data.experience,
        companyCompleted: filteredProjects,
      },
    });

    if (selectedProject?.id === editProjectId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveProject = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...companyCompletedProject };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setCompanyCompletedProject((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddProjectHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(companyCompletedProject).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: companyCompletedProject[next].value,
          };
        },
        {}
      );
      updateData({
        experience: {
          ...data.experience,
          companyCompleted: [...data.experience.companyCompleted, { ...formattedData, id: uuidv4() }],
        },
      });
      resetForm();
      handleModal('addProject');
    }
  };

  const inputChangedHandler = createInputChangedHandler(setCompanyCompletedProject, companyCompletedProject);

  const handleModal = (name: 'addProject') => {
    setShowModal((prevState) => ({
      ...prevState,
      [name]: !prevState[name],
    }));
  };

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove Project'
          message={`You are about to remove ${selectedProject?.projectName} project. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveProject}
          decline={cancelRemoveProject}
        />
      )}
      <p className='block text-sm text-neutralColor-dark flex-1'>
        Please provide the following information for the last 3 projects completed by the company. If the project is still underway, please
        specify the status.
      </p>
      <div className='flex justify-end'>
        <Button label='Add Project' type='button' btnStyle='primary' onClick={() => handleModal('addProject')} />
      </div>
      {data.experience?.companyCompleted.length === 0 && (
        <div className='p-2 mt-2 flex flex-col items-center justify-center gap-2 h-screen/4'>
          <span>
            <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
          </span>
          <p>You have not added any company completed projects</p>
        </div>
      )}
      {data.experience?.companyCompleted.length > 0 && (
        <Accordion multi>
          <div className='space-y-2'>
            {data.experience.companyCompleted.map((project: ICompanyCompletedProject) => (
              <AccordionItem
                key={project.id}
                id={project.id}
                title={
                  <div className='flex justify-between items-center'>
                    <div>
                      <p className='font-bold'>{project.projectName}</p>
                      <span className='text-neutralColor-normal'>
                        {project.streetAddress}, {project.city}, {project.state.label}, {project.zipCode}
                      </span>
                    </div>
                    <div className='flex gap-4'>
                      <div className='cursor-pointer' onClick={() => editProjectHandler(project)}>
                        <PencilIcon className='h-5 w-5 text-neutralColor-normal hover:text-blue-500' />
                      </div>
                      <div className='cursor-pointer' onClick={() => removeProjectHandler(project)}>
                        <TrashIcon className='h-5 w-5 text-neutralColor-normal hover:text-tertiaryColor-normal' />
                      </div>
                    </div>
                  </div>
                }
              >
                <div className='border-t py-5 grid grid-cols-12 gap-5'>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Date Completed</span>
                    <span className='col-span-3'>{project.dateCompleted}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Gross Building Area</span>
                    <span className='col-span-3'>{formatNumber(project.grossBuildingArea ?? 0)}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Number of Housing Units</span>
                    <span className='col-span-3'>{formatNumber(project.housingUnits ?? 0)}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Total Development Cost</span>
                    <span className='col-span-3'>
                      <CurrencyFormatDisplay value={project.grossBuildingArea ?? 0} />
                    </span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Project Type</span>
                    <span className='col-span-3'>{project?.projectTypes?.map((p) => `${p}, `)}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Construction Type</span>
                    <span className='col-span-3'>{project?.constructionTypes?.map((c) => `${c}, `)}</span>
                  </div>
                  <div className='col-span-6 grid grid-cols-6 gap-4'>
                    <span className='col-span-3 text-neutralColor-normal'>Company's Role</span>
                    <span className='col-span-3'>
                      {project?.companyRole?.map((cr) => `${cr}, `)}
                      {project?.partialOwner && (
                        <>
                          Partial Owner(
                          {project?.partialOwner}
                          %)
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </AccordionItem>
            ))}
          </div>
        </Accordion>
      )}

      {showModal.addProject && (
        <Modal customStyle='max-w-full'>
          <h2 className='m-0 mb-4'>Recent Company Completed Project</h2>
          <div className='w-full grid grid-cols-6 gap-4'>
            <Input
              customClassName='col-span-4'
              label='Project Name'
              id='companyCompletedProjectName'
              name='projectName'
              type='text'
              value={companyCompletedProject.projectName.value}
              error={companyCompletedProject?.projectName?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />
            <Input
              customClassName='col-span-6 sm:col-span-2'
              label='Date Completed (U&O)'
              id='companyCompletedDateCompleted'
              name='dateCompleted'
              type='date'
              value={companyCompletedProject.dateCompleted.value}
              error={companyCompletedProject?.dateCompleted?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
            />
            <Input
              customClassName='col-span-6'
              label='Street Address'
              id='companyCompletedStreetAddress'
              name='streetAddress'
              type='text'
              value={companyCompletedProject.streetAddress.value}
              error={companyCompletedProject?.streetAddress?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
            />
            <Input
              customClassName='col-span-6 sm:col-span-2'
              label='City'
              id='companyCompletedCity'
              name='city'
              type='text'
              value={companyCompletedProject.city.value}
              error={companyCompletedProject?.city?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
            />

            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
              <ReactSelect
                label='State / Province'
                id='companyCompletedState'
                name='state'
                options={States.map((s) => ({
                  label: s.name,
                  value: s.name,
                }))}
                value={{
                  label: companyCompletedProject.state.value,
                  value: companyCompletedProject.state.value,
                }}
                error={companyCompletedProject?.state?.error}
                onChange={(option) => {
                  inputChangedHandler((option as ReactSelectOption).value, 'state');
                }}
                onBlur={(_e) => {
                  inputChangedHandler(companyCompletedProject.state.value, 'state');
                }}
              />
            </div>
            <Input
              customClassName='col-span-6 sm:col-span-2'
              label='ZIP / Postal code'
              id='companyCompletedZipCode'
              name='zipCode'
              type='number'
              value={companyCompletedProject.zipCode.value}
              error={companyCompletedProject?.zipCode?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => inputChangedHandler(e.target.value, e.target.name)}
            />
            <div className='col-span-6 sm:col-span-2'>
              <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='companyCompletedGrossBuildingArea'>
                Gross Building Area
              </label>
              <NumberFormat
                id='companyCompletedGrossBuildingArea'
                name='grossBuildingArea'
                value={companyCompletedProject.grossBuildingArea.value ?? ''}
                displayType='input'
                className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                thousandSeparator={true}
                decimalScale={2}
                valueIsNumericString
                onValueChange={(target, s) => {
                  if (s.source !== 'prop') {
                    inputChangedHandler(target.floatValue, 'grossBuildingArea');
                  }
                }}
                onBlur={(e: any) => {
                  inputChangedHandler(companyCompletedProject.grossBuildingArea.value, e.target.name);
                }}
              />
              {companyCompletedProject?.grossBuildingArea?.error && (
                <InputError>{companyCompletedProject?.grossBuildingArea?.error}</InputError>
              )}
            </div>
            <div className='col-span-6 sm:col-span-2'>
              <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='companyCompletedHousingUnits'>
                Number of Housing units (if applicable)
              </label>
              <NumberFormat
                id='companyCompletedHousingUnits'
                name='housingUnits'
                value={companyCompletedProject.housingUnits.value ?? ''}
                className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                thousandSeparator={true}
                decimalScale={2}
                valueIsNumericString
                onValueChange={(target, s) => {
                  if (s.source !== 'prop') {
                    inputChangedHandler(target.floatValue, 'housingUnits');
                  }
                }}
                onBlur={(e: any) => {
                  inputChangedHandler(companyCompletedProject.housingUnits.value, e.target.name);
                }}
              />
              {companyCompletedProject?.housingUnits?.error && <InputError>{companyCompletedProject?.housingUnits?.error}</InputError>}
            </div>
            <div className='col-span-6 sm:col-span-2'>
              <label
                className={'leading-7 block text-sm font-medium text-neutralColor-medium'}
                htmlFor='companyCompletedTotalDevelopmentCost'
              >
                Total Development Cost
              </label>
              <NumberFormat
                name='developmentCost'
                id='companyCompletedTotalDevelopmentCost'
                value={companyCompletedProject.developmentCost.value ?? ''}
                className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
                thousandSeparator={true}
                decimalScale={2}
                valueIsNumericString
                prefix={'$ '}
                maxLength={21}
                onValueChange={(target, s) => {
                  if (s.source !== 'prop') {
                    inputChangedHandler(target.floatValue, 'developmentCost');
                  }
                }}
                onBlur={(e: any) => {
                  inputChangedHandler(companyCompletedProject.developmentCost.value, e.target.name);
                }}
              />
              {companyCompletedProject?.developmentCost?.error && (
                <InputError>{companyCompletedProject?.developmentCost?.error}</InputError>
              )}
            </div>

            <div className='col-span-6 md:col-span-2'>
              <CheckboxGroup
                label='Project Type (check all that apply):'
                id='companyCompletedProjectTypes'
                name='projectTypes'
                options={projectTypes.map((p) => ({
                  label: p.label,
                  value: p.label,
                }))}
                value={companyCompletedProject?.projectTypes.value || []}
                error={companyCompletedProject?.projectTypes?.error}
                onChange={(value) => inputChangedHandler(value, 'projectTypes')}
              />
            </div>
            <div className='col-span-6 md:col-span-2'>
              <CheckboxGroup
                label='Construction Type (check all that apply):'
                id='companyCompletedConstructionTypes'
                name='constructionTypes'
                options={constructionTypes.map((c) => ({
                  label: c.label,
                  value: c.label,
                }))}
                value={companyCompletedProject.constructionTypes.value || []}
                error={companyCompletedProject?.constructionTypes?.error}
                onChange={(value) => inputChangedHandler(value, 'constructionTypes')}
              />
            </div>
            <div className='col-span-6 md:col-span-2'>
              <CheckboxGroup
                label="Please select the company's role (Check all that apply):"
                id='companyCompletedCompanyRoles'
                name='companyRole'
                options={companyRoles.map((c) => ({
                  label: c.label,
                  value: c.label,
                }))}
                value={companyCompletedProject.companyRole.value || []}
                error={companyCompletedProject?.companyRole?.error}
                onChange={(value) => inputChangedHandler(value, 'companyRole')}
              />

              <fieldset className='space-y-2 mt-2'>
                <div className='relative flex items-start'>
                  <div className='text-sm flex w-full gap-2'>
                    <label htmlFor='companyCompletedPartialOwner' className='font-medium text-gray-700'>
                      Partial Owner (%):
                    </label>
                    <input
                      type='number'
                      id='companyCompletedPartialOwner'
                      name='partialOwner'
                      autoComplete='companyCompletedPartialOwner'
                      className='block w-24 border-b border-gray-300 shadow-sm px-3 focus:outline-hidden focus:ring-green-500 focus:border-green-500 sm:text-sm'
                      value={companyCompletedProject.partialOwner.value ?? ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        inputChangedHandler(parseFloat(e.target.value), e.target.name);
                      }}
                    />
                    <span>%</span>
                  </div>
                </div>
                {companyCompletedProject?.partialOwner?.error && <InputError>{companyCompletedProject?.partialOwner?.error}</InputError>}
              </fieldset>
            </div>
          </div>
          <div className='w-full flex justify-end gap-4 mt-10'>
            <Button
              label='Cancel'
              type='button'
              btnStyle='neutral'
              onClick={() => {
                resetForm();
                setIsEditing(false);
                handleModal('addProject');
              }}
            />
            <Button
              label={`${isEditing ? 'Save' : 'Add Project'}`}
              type='button'
              btnStyle='primary'
              onClick={isEditing ? updateProjectHandler : onAddProjectHandler}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default CompanyCompletedProjectForm;
